import { Button } from 'components/button/Button'
import { TwoLineButtonContent } from 'components/button/TwoLineButtonContent'
import { useModal } from 'components/modal/Modal'
import { UpsellModal } from 'components/modal/upsellModal/UpsellModal'
import { Picture } from 'components/picture/Picture'
import { useDirectiveUpsellPlanType } from 'logic/subscription/upsell/useDirectiveUpsellPlanType'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { events, fireEvent } from 'utils/analytics'
import { EventArgs } from 'utils/analytics/types'
import { useRunOnceReady } from 'utils/hooks'
import { pxToRem } from 'utils/styled'
import {
  SAlignedPicture,
  SDirectiveUpgradeModal,
  SReceiverImg,
  SText,
} from './DirectivePaywallModal.styled'

export const useDirectivePaywallModal = ({
  eventArgs,
  isOwner,
  ownerName,
}: {
  eventArgs: EventArgs['directive_paywall_hit']
  next?: string
  isOwner?: boolean
  ownerName?: string | null
}) => {
  const { t } = useTranslation('directive')
  const upsellPlanType = useDirectiveUpsellPlanType()
  const [modal, openModal, closeModal, isOpen] = useModal(({ close, isOpen }) =>
    isOpen ? (
      isOwner ? (
        <UpsellModal
          close={close}
          title={t('directive.upsell_modal.title', {
            plan: upsellPlanType ? t(`directive.upsell_modal.${upsellPlanType}.display_name`) : '',
          })}
          picture={<Picture imageKey="illustrations/locked" sizes="160px" />}
          primaryButtonText={
            <TwoLineButtonContent
              line1={t('directive.upsell_modal.button_line_1')}
              line2={t('directive.upsell_modal.button_line_2')}
            />
          }
          secondaryButton={
            <Link href="/pricing" passHref legacyBehavior>
              <a
                onClick={() => {
                  fireEvent('directive_paywall_continue', {
                    option: 'plan_upgrade',
                  })
                }}
              >
                <SText variant="bodySmall" css={{ width: '100%' }}>
                  <u>{t('directive.upsell_modal.secondary_button')}</u>
                </SText>
              </a>
            </Link>
          }
          paywallHitEventArgs={'no-trigger'}
          buttonOrientation={'vertical'}
        />
      ) : (
        <SDirectiveUpgradeModal>
          <SAlignedPicture>
            <Picture
              imageKey={'illustrations/locked'}
              sizes={'200px'}
              imageComponent={SReceiverImg}
              alt={'GoodTrust'}
            />
          </SAlignedPicture>

          <SText variant="bodyLargeBold" color="black">
            {t('directive.upsell_modal.pdf_locked_text')}
          </SText>
          <SText>
            {t('directive.upsell_modal.pdf_locked_explanation', {
              ownerName: ownerName ?? t('directive.common.owner'),
            })}
          </SText>
          <Button
            type="button"
            size="large"
            onClick={() => close()}
            css={{ width: `${pxToRem(178)}rem`, margin: '1rem auto 0' }}
          >
            {t('directive.upsell_modal.button')}
          </Button>
        </SDirectiveUpgradeModal>
      )
    ) : null
  )

  useRunOnceReady(isOpen, () => {
    events.directive_paywall_hit.fire(eventArgs)
  })

  return [modal, openModal, closeModal] as const
}
